import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	restrictToHorizontalAxis,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type {
	ColumnOrderState,
	ColumnVisibilityState,
	PaginationState,
	Row,
	RowData,
	SortingState,
} from "@tanstack/react-table";
import type { Virtualizer } from "@tanstack/react-virtual";
import { RefreshCw01, SearchMd } from "@untitledui/icons";
import {
	type CSSProperties,
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/Atoms/Input/input";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTableColumnSizing } from "@/hooks/use-table-column-sizing";
import { cx } from "@/utils/cx";
import { isModifiedRowClick } from "@/utils/table-row-click";
import { FilterDrawer } from "../FilterDrawer/FilterDrawer";
import { TableActions } from "../TableActions/TableActions";
import type { UseDataTableConfig } from ".";
import { wrapAccessTableCellContent } from "./access-table-cell";
import { type AppTableFeatures, EMPTY_TABLE_DATA } from "./app-table-features";
import { CustomTable, CustomTableCard } from "./CustomTable";
import { DeferredColumnResizer } from "./DeferredColumnResizer";
import { DraggableCellContent } from "./DragAlongCell";
import {
	DraggableColumnContent,
	DragOverlayHeader,
} from "./DraggableTableHeader";
import { SortableColumnItem } from "./SortableColumnItem";
import {
	TableFilterBar,
	TableFilterBarDisabledContext,
	TableFilterBarType,
	type TableFilterBarType as TableFilterBarTypeValue,
} from "./TableFilterBar";
import { TablePagination } from "./TablePagination";
import { wrapTableCellClamp } from "./table-cell-clamp";
import { getTableColumnLayoutStyle } from "./table-column-layout";
import {
	getTableColumnMeta,
	isActionsColumnId,
	isColumnReorderDisabled,
	isColumnReorderLockedById,
	isColumnVisibilityToggleDisabled,
	isIconGutterColumn,
	isRowHeaderColumn,
	isSelectionGutterColumn,
} from "./table-column-meta";
import { tableHeaderCellSortableFromColumn } from "./table-header-cell";
import { useAppTable } from "./use-app-table";

export interface CustomTableWrapperProps<TData extends RowData>
	extends UseDataTableConfig<TData> {
	// ============ Display ============
	/** Size variant for the table */
	size?: "sm" | "md";
	/** Whether to wrap the table in a card */
	showCard?: boolean;
	/** Card title */
	title?: string;
	/** Card description */
	description?: string;
	/** Badge to show next to the title */
	badge?: ReactNode | string;
	/** Content to show in the card header trailing area */
	headerTrailingContent?: ReactNode;
	/** Whether to show the padding on the table card header */
	showHeaderPadding?: boolean;
	/** Additional CSS classes for the table */
	className?: string;
	/** Additional CSS classes for the card */
	cardClassName?: string;
	/** Whether to enable zebra striping on table rows */
	zebraStriping?: boolean;

	// ============ Loading States ============
	/** Whether data is currently loading */
	isLoading?: boolean;
	/** Whether data is currently being fetched (background refresh) */
	isFetching?: boolean;
	/** Whether a manual refresh is in progress */
	isRefreshing?: boolean;
	/** Callback for manual refresh action */
	onRefresh?: () => void;
	/** Show refresh button in header */
	showRefreshButton?: boolean;
	/** Custom refresh button component */
	refreshButton?: ReactNode;
	/** Custom loading state component */
	loadingState?: ReactNode;
	/** Custom fetching state component */
	fetchingState?: ReactNode;
	/** Custom empty state component */
	emptyState?: ReactNode;

	// ============ Pagination ============
	/** Whether to show the pagination */
	showPagination?: boolean;
	/** Pagination mode - 'client' for internal state, 'server' for external control */
	paginationMode?: "client" | "server";
	/** Externally controlled pagination state (used when paginationMode='server') */
	externalPaginationState?: {
		pageSize: number;
		pageIndex?: number;
	};
	/** Callback fired when pagination state changes (used when paginationMode='server') */
	onPaginationChange?: (state: { pageSize: number; pageIndex: number }) => void;
	/** Total page count for server-side pagination */
	pageCount?: number;
	/**
	 * Row count for the current result set (search + filters).
	 * Drives the "Showing X-Y of Z" label on {@link TablePagination}.
	 */
	rowCount?: number;
	/**
	 * Unfiltered baseline row count (before search/filters).
	 * Used for empty vs empty-search detection only — not for pagination "of Z".
	 */
	totalRowCount?: number;

	// ============ Sorting ============
	/** Mapping of column IDs to API field names for server-side sorting */
	sortFieldMapping?: Record<string, string[]>;
	/** Callback fired when sorting changes (when sortFieldMapping is provided) */
	onSortChange?: (params: { fields: string[]; descending: boolean }) => void;
	/** Default column ID to sort by */
	defaultSortField?: string;
	/** Default sort direction @default false (ascending) */
	defaultSortDescending?: boolean;

	// ============ Filtering ============
	/** Current search/filter value for the filter drawer */
	filterValue?: string;
	/** Callback when filter value changes */
	onFilterChange?: (value: string) => void;
	/** Callback fired when the filter value changes after debouncing */
	onDebouncedFilterChange?: (value: string) => void;
	/** Debounce delay in milliseconds for filter changes @default 500 */
	filterDebounceMs?: number;
	/** Placeholder text for the filter input */
	filterPlaceholder?: string;
	/** Content to show in the trailing area of the filter drawer */
	filtersTrailingContent?: ReactNode;
	/**
	 * Extra trailing content after workspace TableActions (e.g. Add button).
	 * Only rendered when `filterLayout="bar"` and `filterBarType="workspace"`.
	 */
	filterBarTrailing?: ReactNode;
	/** Extra class names on the TableFilterBar root when `filterLayout="bar"`. */
	filterBarClassName?: string;
	/** Optional test id passed to the filter search input */
	filterInputTestId?: string;
	/** Max width of the filter search input (percent of table row) when no trailing filters @default 45 */
	filterSearchMaxWidthPercent?: number;
	/**
	 * Filter chrome layout.
	 * - `drawer` — collapsible FilterDrawer + toolbar TableActions (legacy)
	 * - `bar` — always-visible Figma TableFilterBar (search + filters)
	 * @default "drawer"
	 */
	filterLayout?: "drawer" | "bar";
	/**
	 * Figma TableFilterBar Type when `filterLayout="bar"`.
	 * @default "landing"
	 */
	filterBarType?: TableFilterBarTypeValue;

	// ============ Table Actions ============
	/** Enable table actions (filter, view options, export) in the header */
	enableTableActions?: boolean;
	/** Whether the filter drawer is currently open */
	isFilterOpen?: boolean;
	/** Whether any non-search filters are currently applied (e.g. column filters) */
	hasActiveFilters?: boolean;
	/** Callback when filter button is clicked */
	onFilterClick?: () => void;
	/** Callback when export button is clicked */
	onExportClick?: (visibleColumns?: string[]) => void;
	/** Whether export is currently in progress */
	isExportLoading?: boolean;
	/**
	 * Host element for landing-page TableActions (Figma `_TableOptions` 4962:186000).
	 * When set with `filterBarType="landing"`, portals the options menu (Download +
	 * Manage Columns) into PageHeader via `overflowControl`.
	 */
	tableOptionsPortalTarget?: HTMLElement | null;

	// ============ Features ============
	/** Enable column reordering via Manage Columns dropdown */
	enableColumnReordering?: boolean;
	/**
	 * Show drag handles on table header cells and allow header drag-reorder.
	 * Off by default — prefer Manage Columns when `enableColumnReordering` is on.
	 * @default false
	 */
	enableHeaderColumnReordering?: boolean;
	/** Enable column resizing with deferred updates */
	enableColumnResizing?: boolean;
	/** Callback fired when a row is activated (click includes event for modifier keys; keyboard omits event) */
	onRowClick?: (row: TData, event?: MouseEvent<HTMLTableRowElement>) => void;

	// ============ Persistence ============
	/** Initial column order (for persisted state) */
	initialColumnOrder?: string[];
	/** Initial column visibility (for persisted state) */
	initialColumnVisibility?: ColumnVisibilityState;
	/**
	 * Product-default visibility restored by Manage columns → Reset
	 * (e.g. `{ id: false }` for default-hidden columns).
	 * @default {}
	 */
	defaultColumnVisibility?: ColumnVisibilityState;
	/** Initial sorting state (for persisted state) */
	initialSortingState?: SortingState;
	/** Whether the persisted settings have been loaded from the API */
	settingsLoaded?: boolean;
	/** Callback fired when column order changes (for persistence) */
	onColumnOrderChange?: (columnOrder: string[]) => void;
	/** Callback fired when column visibility changes (for persistence) */
	onColumnVisibilityChange?: (visibility: ColumnVisibilityState) => void;
	/** Callback fired when sorting state changes (for persistence) */
	onSortingStateChange?: (sorting: SortingState) => void;

	// ============ Row Selection ============
	/** Controlled selection state (React Aria) - set of selected row IDs */
	selectedKeys?: Set<string> | "all";
	/**
	 * When set, pagination uses "Showing N of M · K selected".
	 * Leave unset for the range label used by other tables.
	 */
	selectedCount?: number;
	/** Callback when selection changes (React Aria) */
	onSelectionChange?: (keys: Set<string>) => void;
	/** Function to get a unique row ID for selection */
	getRowId?: (row: TData) => string;
	/**
	 * When true for a row, allow the row and its cells to grow past the default
	 * 50px height so multi-line stacked content is fully visible. Default: fixed 50px.
	 */
	getRowAutoHeight?: (row: TData) => boolean;
	/**
	 * Row id to paint as the open-drawer “active” row (not checkbox selection).
	 * Prefer `ActiveDrawerRow.Table` over passing this directly.
	 */
	activeRowId?: string | null;

	// ============ Virtualization ============
	virtualizer?: Virtualizer<HTMLDivElement | HTMLTableElement, Element>;
	/** Whether this is a virtualized table */
	isVirtualTable?: boolean;

	/**
	 * Stable unique key for TanStack Table Devtools.
	 * Falls back to `title` or `"app-table"` when omitted.
	 */
	tableKey?: string;
}

export function CustomTableWrapper<TData extends RowData>({
	// Table configuration
	data,
	columns,
	enableSorting = true,
	enableRowSelection = false,
	initialSorting = [],
	tableOptions = {},
	onSortingChange,

	// Display configuration
	size = "md",
	showCard = true,
	title,
	description,
	headerTrailingContent,
	badge,
	emptyState,
	loadingState,
	isLoading = false,
	isFetching = false,
	isRefreshing = false,
	onRefresh,
	// fetchingState, // TODO: Implement custom fetching state
	showRefreshButton = false,
	refreshButton,
	className,
	cardClassName,
	onRowClick,
	showPagination = true,
	showHeaderPadding = true,
	// Zebra striping configuration
	zebraStriping = true,
	// Virtualization configuration
	virtualizer,
	isVirtualTable = false,
	// Pagination configuration (for TanStack Query integration)
	paginationMode = "client",
	externalPaginationState,
	onPaginationChange,
	pageCount: externalPageCount,
	// TableActions configuration
	enableTableActions = false,
	isFilterOpen = false,
	hasActiveFilters = false,
	onFilterClick,
	onExportClick,
	isExportLoading = false,
	tableOptionsPortalTarget = null,
	// FilterDrawer configuration
	filterValue = "",
	onFilterChange,
	filterPlaceholder,
	filtersTrailingContent,
	filterBarTrailing,
	filterBarClassName,
	filterInputTestId,
	filterSearchMaxWidthPercent,
	filterLayout = "drawer",
	filterBarType = TableFilterBarType.Landing,
	// Column reordering configuration
	enableColumnReordering = false,
	enableHeaderColumnReordering = false,
	// Column resizing configuration
	enableColumnResizing = false,
	// Server-side sorting configuration
	sortFieldMapping,
	onSortChange,
	defaultSortField,
	defaultSortDescending = false,
	// Debounced filter configuration
	onDebouncedFilterChange,
	filterDebounceMs = 500,
	rowCount,
	totalRowCount,
	// Row selection (React Aria)
	selectedKeys,
	selectedCount,
	onSelectionChange,
	getRowId,
	getRowAutoHeight,
	activeRowId = null,
	// Persistence configuration
	initialColumnOrder,
	initialColumnVisibility,
	defaultColumnVisibility = {},
	initialSortingState,
	settingsLoaded = false,
	onColumnOrderChange,
	onColumnVisibilityChange,
	onSortingStateChange,
	tableKey,
}: CustomTableWrapperProps<TData>) {
	const { t } = useAppTranslation();
	const resolvedTableKey = tableKey ?? title ?? "app-table";
	/** Column order state when Manage Columns and/or header drag-reorder is enabled */
	const usesColumnOrder =
		enableColumnReordering || enableHeaderColumnReordering;
	// Initialize sorting state - prefer initialSortingState (persisted), then defaultSortField, then initialSorting
	const [sorting, setSorting] = useState<SortingState>(() => {
		// If we have persisted sorting state, use it
		if (initialSortingState && initialSortingState.length > 0) {
			return initialSortingState;
		}
		// Otherwise use default sort field if sortFieldMapping is provided
		if (sortFieldMapping && defaultSortField) {
			return [{ id: defaultSortField, desc: defaultSortDescending }];
		}
		return initialSorting;
	});
	const pageSizeOptions = [10, 25, 50, 100];

	// Ref for the table container (used for column sizing calculations)
	const tableContainerRef = useRef<HTMLDivElement>(null);
	/** Avoid double-firing row activation when React Aria invokes both pointer and onAction */
	const rowPointerActivationIds = useRef(new Set<string>());

	const rowFullViewAction = useMemo(() => {
		for (const col of columns) {
			const handler = getTableColumnMeta(col.meta)?.rowFullViewAction;
			if (handler) return handler;
		}
		return undefined;
	}, [columns]);

	const activateRow = useCallback(
		(rowData: TData, event?: MouseEvent<HTMLTableRowElement>) => {
			if (isModifiedRowClick(event) && rowFullViewAction) {
				rowFullViewAction(rowData);
			} else if (onRowClick) {
				onRowClick(rowData, event);
			}
		},
		[onRowClick, rowFullViewAction],
	);

	const isRowActivatable = Boolean(onRowClick || rowFullViewAction);

	// Use internal state for 'client' mode, external state for 'server' mode
	const [internalPagination, setInternalPagination] = useState<PaginationState>(
		{
			pageIndex: externalPaginationState?.pageIndex ?? 0,
			pageSize: externalPaginationState?.pageSize ?? pageSizeOptions[0],
		},
	);

	// Determine which pagination state to use
	const pagination: PaginationState = useMemo(() => {
		if (paginationMode === "server" && externalPaginationState) {
			return {
				pageIndex: externalPaginationState.pageIndex ?? 0,
				pageSize: externalPaginationState.pageSize,
			};
		}
		if (paginationMode === "client" && externalPaginationState) {
			return {
				pageIndex: externalPaginationState.pageIndex ?? 0,
				pageSize: externalPaginationState.pageSize,
			};
		}
		return internalPagination;
	}, [paginationMode, externalPaginationState, internalPagination]);

	// Handler for pagination changes that respects the mode
	const handlePaginationChange = useCallback(
		(
			updater: PaginationState | ((prev: PaginationState) => PaginationState),
		) => {
			const newState =
				typeof updater === "function" ? updater(pagination) : updater;

			if (paginationMode === "server") {
				onPaginationChange?.(newState);
			} else if (paginationMode === "client" && externalPaginationState) {
				onPaginationChange?.(newState);
			} else {
				setInternalPagination(newState);
			}
		},
		[pagination, paginationMode, onPaginationChange, externalPaginationState],
	);

	// Internal filter value state for immediate UI updates
	const [internalFilterValue, setInternalFilterValue] = useState(filterValue);
	/** Bar layout: Hide/Show Filters toggles inline filter chips (Figma `_TableOptions`). */
	const [areInlineFiltersVisible, setAreInlineFiltersVisible] = useState(true);

	// Debounced filter value for API calls
	const lastEmittedFilterValue = useRef<string>(filterValue);
	const onDebouncedFilterChangeRef = useRef(onDebouncedFilterChange);
	onDebouncedFilterChangeRef.current = onDebouncedFilterChange;

	// Sync internal state with external filterValue prop when it changes
	useEffect(() => {
		setInternalFilterValue(filterValue);
		lastEmittedFilterValue.current = filterValue;
	}, [filterValue]);

	useEffect(() => {
		// Skip if no debounced callback or value hasn't changed
		if (!onDebouncedFilterChangeRef.current) return;
		if (internalFilterValue === lastEmittedFilterValue.current) return;

		// Clearing search should exit filter mode immediately (no debounce lag).
		if (internalFilterValue === "") {
			lastEmittedFilterValue.current = "";
			onDebouncedFilterChangeRef.current?.("");
			return;
		}

		const timeout = setTimeout(() => {
			lastEmittedFilterValue.current = internalFilterValue;
			onDebouncedFilterChangeRef.current?.(internalFilterValue);
		}, filterDebounceMs);

		return () => clearTimeout(timeout);
	}, [internalFilterValue, filterDebounceMs]);

	// Handler for immediate filter changes (updates internal state and optionally notifies parent)
	const handleFilterInputChange = useCallback(
		(value: string) => {
			setInternalFilterValue(value);
			// Also call the immediate change handler if provided (for legacy/controlled usage)
			onFilterChange?.(value);
		},
		[onFilterChange],
	);

	// Handle sorting changes - emit API-ready params when sortFieldMapping is provided
	const handleInternalSortingChange = useCallback(
		(newSorting: SortingState) => {
			setSorting(newSorting);

			// If sortFieldMapping is provided, emit API-ready params
			if (sortFieldMapping && onSortChange) {
				if (newSorting.length === 0) {
					// Use default sort when cleared
					const defaultFields = defaultSortField
						? sortFieldMapping[defaultSortField]
						: undefined;
					if (defaultFields) {
						onSortChange({
							fields: defaultFields,
							descending: defaultSortDescending,
						});
					}
				} else {
					const sortColumn = newSorting[0];
					const apiFields = sortFieldMapping[sortColumn.id];
					if (apiFields) {
						onSortChange({
							fields: apiFields,
							descending: sortColumn.desc,
						});
					}
				}

				// Reset pagination to first page on sort change (server mode)
				if (paginationMode === "server") {
					handlePaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
				}
			}

			// Also call the legacy onSortingChange if provided
			onSortingChange?.(newSorting);
		},
		[
			sortFieldMapping,
			onSortChange,
			defaultSortField,
			defaultSortDescending,
			paginationMode,
			handlePaginationChange,
			onSortingChange,
		],
	);

	// Column visibility state - use persisted state if available
	const [columnVisibility, setColumnVisibility] =
		useState<ColumnVisibilityState>(() => initialColumnVisibility ?? {});

	// Column order state for drag and drop reordering - use persisted state if available
	const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
		if (initialColumnOrder && initialColumnOrder.length > 0) {
			return initialColumnOrder;
		}
		return columns.map((col) => col.id as string);
	});

	// ============ Sync state when persisted settings load from API ============
	// Track if we've already applied persisted settings to avoid re-syncing
	const hasAppliedPersistedSettings = useRef(false);

	// Sync all persisted settings when settingsLoaded becomes true
	useEffect(() => {
		// Only apply once when settings are first loaded
		if (!settingsLoaded || hasAppliedPersistedSettings.current) {
			return;
		}

		hasAppliedPersistedSettings.current = true;

		// Apply sorting state
		if (initialSortingState && initialSortingState.length > 0) {
			setSorting(initialSortingState);

			// Also update the API sort params if sortFieldMapping is provided
			if (sortFieldMapping && onSortChange) {
				const sortColumn = initialSortingState[0];
				const apiFields = sortFieldMapping[sortColumn.id];
				if (apiFields) {
					onSortChange({
						fields: apiFields,
						descending: sortColumn.desc,
					});
				}
			}
		}

		// Apply column order
		if (initialColumnOrder && initialColumnOrder.length > 0) {
			setColumnOrder(initialColumnOrder);
		}

		// Apply column visibility
		if (
			initialColumnVisibility &&
			Object.keys(initialColumnVisibility).length > 0
		) {
			setColumnVisibility(initialColumnVisibility);
		}
	}, [
		settingsLoaded,
		initialSortingState,
		initialColumnOrder,
		initialColumnVisibility,
		sortFieldMapping,
		onSortChange,
	]);

	// Sync column order when columns change
	useEffect(() => {
		const columnIds = columns.map((col) => col.id as string);
		setColumnOrder((prev) => {
			// Keep existing order for columns that still exist, add new columns at the end
			const existingOrder = prev.filter((id) => columnIds.includes(id));
			const newColumns = columnIds.filter((id) => !prev.includes(id));
			return [...existingOrder, ...newColumns];
		});
	}, [columns]);

	// Use the column sizing hook to auto-calculate column widths (only when resizing is enabled)
	const {
		columnSizing,
		handleColumnSizingChange,
		resetColumnSizing,
		isInitialized,
		flexColumnId,
		lastVisibleColumnId,
	} = useTableColumnSizing({
		columns,
		containerRef: tableContainerRef,
		columnVisibility,
		columnOrder,
		enabled: enableColumnResizing,
	});

	// Dnd-kit sensors for Manage Columns + optional header column drag
	const sensors = useSensors(
		useSensor(PointerSensor, {}),
		useSensor(KeyboardSensor, {}),
	);

	// Track the currently dragged column ID (header drag only)
	const [activeColumnId, setActiveColumnId] = useState<string | null>(null);

	// Force re-render counter for column resizing (only used when resizing is enabled)
	const [resizeVersion, setResizeVersion] = useState(0);
	const initialSizeApplied = useRef(false);
	/** Columns the user resized — lifts max-width until Manage Columns → Reset. */
	const [manuallyResizedColumnIds, setManuallyResizedColumnIds] = useState(
		() => new Set<string>(),
	);

	/** Only user-resized columns contribute controlled pixel widths to the table. */
	const appliedColumnSizing = useMemo(() => {
		if (!enableColumnResizing || manuallyResizedColumnIds.size === 0) {
			return {} as typeof columnSizing;
		}
		const next: typeof columnSizing = {};
		for (const id of manuallyResizedColumnIds) {
			if (columnSizing[id] != null) {
				next[id] = columnSizing[id];
			}
		}
		return next;
	}, [columnSizing, enableColumnResizing, manuallyResizedColumnIds]);

	// Force table re-render when initial column sizing is calculated (once only, only when enabled)
	useEffect(() => {
		if (!enableColumnResizing) return;

		if (
			isInitialized &&
			Object.keys(columnSizing).length > 0 &&
			!initialSizeApplied.current
		) {
			initialSizeApplied.current = true;
			setResizeVersion((v) => v + 1);
		}
	}, [enableColumnResizing, isInitialized, columnSizing]);

	// Track layout changes that affect column width allocation
	const previousColumnLayoutRef = useRef<string | null>(null);
	const columnLayoutKey = `${flexColumnId ?? "none"}:${lastVisibleColumnId ?? "none"}`;

	// Force table re-render when flex/last column roles change (reorder, visibility)
	useEffect(() => {
		if (!enableColumnResizing) return;

		if (previousColumnLayoutRef.current === null) {
			previousColumnLayoutRef.current = columnLayoutKey;
			return;
		}

		if (previousColumnLayoutRef.current !== columnLayoutKey) {
			previousColumnLayoutRef.current = columnLayoutKey;
			setResizeVersion((v) => v + 1);
		}
	}, [enableColumnResizing, columnLayoutKey]);

	// ============ Persistence Callbacks ============
	// Use refs to store callbacks and previous values to avoid unnecessary effect triggers
	const onSortingStateChangeRef = useRef(onSortingStateChange);
	const onColumnOrderChangeRef = useRef(onColumnOrderChange);
	const onColumnVisibilityChangeRef = useRef(onColumnVisibilityChange);

	// Keep refs updated with latest callbacks
	useEffect(() => {
		onSortingStateChangeRef.current = onSortingStateChange;
	}, [onSortingStateChange]);
	useEffect(() => {
		onColumnOrderChangeRef.current = onColumnOrderChange;
	}, [onColumnOrderChange]);
	useEffect(() => {
		onColumnVisibilityChangeRef.current = onColumnVisibilityChange;
	}, [onColumnVisibilityChange]);

	// Track previous values to only call callbacks when values actually change
	const prevSortingRef = useRef<string>(JSON.stringify(sorting));
	const prevColumnOrderRef = useRef<string>(JSON.stringify(columnOrder));
	const prevColumnVisibilityRef = useRef<string>(
		JSON.stringify(columnVisibility),
	);

	// Track if initial render
	const isInitialRender = useRef(true);

	// Call onSortingStateChange when sorting changes (for persistence)
	useEffect(() => {
		if (isInitialRender.current) return;
		const currentValue = JSON.stringify(sorting);
		if (currentValue !== prevSortingRef.current) {
			prevSortingRef.current = currentValue;
			onSortingStateChangeRef.current?.(sorting);
		}
	}, [sorting]);

	// Call onColumnOrderChange when column order changes (for persistence)
	useEffect(() => {
		if (isInitialRender.current) return;
		const currentValue = JSON.stringify(columnOrder);
		if (currentValue !== prevColumnOrderRef.current) {
			prevColumnOrderRef.current = currentValue;
			onColumnOrderChangeRef.current?.(columnOrder);
		}
	}, [columnOrder]);

	// Call onColumnVisibilityChange when column visibility changes (for persistence)
	useEffect(() => {
		if (isInitialRender.current) return;
		const currentValue = JSON.stringify(columnVisibility);
		if (currentValue !== prevColumnVisibilityRef.current) {
			prevColumnVisibilityRef.current = currentValue;
			onColumnVisibilityChangeRef.current?.(columnVisibility);
		}
	}, [columnVisibility]);

	// Mark initial render as complete after first render cycle
	useEffect(() => {
		isInitialRender.current = false;
	}, []);

	// Handle drag start - store which column is being dragged (header)
	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveColumnId(event.active.id as string);
	}, []);

	// Handle drag end for column reordering (table header)
	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setActiveColumnId(null);
			const { active, over } = event;
			if (!active || !over || active.id === over.id) return;

			const activeId = active.id as string;
			const overId = over.id as string;
			if (
				isColumnReorderLockedById(activeId, columns) ||
				isColumnReorderLockedById(overId, columns)
			) {
				return;
			}

			setColumnOrder((currentOrder) => {
				const oldIndex = currentOrder.indexOf(activeId);
				const newIndex = currentOrder.indexOf(overId);
				return arrayMove(currentOrder, oldIndex, newIndex);
			});
		},
		[columns],
	);

	// Handle drag end for column reordering (view options popover)
	const handlePopoverDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!active || !over || active.id === over.id) return;

			const activeId = active.id as string;
			const overId = over.id as string;
			if (
				isColumnReorderLockedById(activeId, columns) ||
				isColumnReorderLockedById(overId, columns)
			) {
				return;
			}

			setColumnOrder((currentOrder) => {
				const oldIndex = currentOrder.indexOf(activeId);
				const newIndex = currentOrder.indexOf(overId);
				return arrayMove(currentOrder, oldIndex, newIndex);
			});
		},
		[columns],
	);

	// Destructure state from tableOptions to handle it separately
	const tableOptionsState = tableOptions.state;
	const restTableOptions = useMemo(() => {
		const { state: _, ...rest } = tableOptions;
		return rest;
	}, [tableOptions]);

	const tableData = data ?? EMPTY_TABLE_DATA;

	const table = useAppTable({
		key: resolvedTableKey,
		data: tableData as TData[],
		columns,
		enableColumnResizing,
		...(getRowId && {
			getRowId: (row: TData) => getRowId(row),
		}),
		...(!isVirtualTable && {
			onPaginationChange: handlePaginationChange,
			...(paginationMode === "server" && {
				manualPagination: true,
				pageCount: externalPageCount ?? -1,
			}),
		}),
		...(enableSorting && {
			enableSorting: true,
			// Stable cycle: unsorted → asc → desc → unsorted (see use-app-table).
			sortDescFirst: false,
			enableSortingRemoval: true,
			manualSorting: sortFieldMapping
				? true
				: (tableOptions.manualSorting ?? false),
			onSortingChange: (updater) => {
				const currentSorting = sortFieldMapping
					? sorting
					: tableOptions.manualSorting
						? ((tableOptionsState?.sorting as SortingState) ?? sorting)
						: sorting;
				const newSorting =
					typeof updater === "function" ? updater(currentSorting) : updater;

				if (sortFieldMapping) {
					handleInternalSortingChange(newSorting);
				} else {
					if (!tableOptions.manualSorting) {
						setSorting(newSorting);
					}
					onSortingChange?.(newSorting);
				}
			},
		}),
		...restTableOptions,
		onColumnVisibilityChange: setColumnVisibility,
		...(usesColumnOrder && {
			onColumnOrderChange: setColumnOrder,
		}),
		...(enableColumnResizing && {
			onColumnSizingChange: handleColumnSizingChange,
		}),
		state: {
			...tableOptionsState,
			sorting: sortFieldMapping
				? sorting
				: tableOptions.manualSorting
					? ((tableOptionsState?.sorting as SortingState) ?? sorting)
					: sorting,
			...(!isVirtualTable && { pagination }),
			columnVisibility,
			...(usesColumnOrder && { columnOrder }),
			...(enableColumnResizing && { columnSizing: appliedColumnSizing }),
		},
	});

	// Handle column resize commit - called when user releases the resize handle
	// Updates our hook's state directly (which flows to TanStack via controlled state)
	const handleResizeCommit = useCallback(
		(columnId: string, newWidth: number) => {
			setManuallyResizedColumnIds((prev) => {
				const next = new Set(prev);
				next.add(columnId);
				return next;
			});
			// Update our hook's state - this calculates the last column width automatically
			handleColumnSizingChange((prev) => ({
				...prev,
				[columnId]: newWidth,
			}));
			// Force a re-render to ensure the table updates visually
			setResizeVersion((v) => v + 1);
		},
		[handleColumnSizingChange],
	);

	const handleResizeReset = useCallback((columnId: string) => {
		setManuallyResizedColumnIds((prev) => {
			if (!prev.has(columnId)) return prev;
			const next = new Set(prev);
			next.delete(columnId);
			return next;
		});
		setResizeVersion((v) => v + 1);
	}, []);

	/**
	 * Factory default sort — same resolution as initial state when nothing is persisted.
	 * Manage Columns → Reset restores this (and persists via onSortingStateChange).
	 */
	const getDefaultSortingState = useCallback((): SortingState => {
		if (sortFieldMapping && defaultSortField) {
			return [{ id: defaultSortField, desc: defaultSortDescending }];
		}
		return initialSorting;
	}, [
		sortFieldMapping,
		defaultSortField,
		defaultSortDescending,
		initialSorting,
	]);

	// Reset column visibility, order, widths, and sorting (restores auto + max-width 215)
	const handleResetColumns = useCallback(() => {
		setColumnVisibility({ ...defaultColumnVisibility });
		setColumnOrder(columns.map((col) => col.id as string));
		setManuallyResizedColumnIds(new Set());
		if (enableColumnResizing) {
			resetColumnSizing();
			setResizeVersion((v) => v + 1);
		}

		const defaultSortingState = getDefaultSortingState();
		if (sortFieldMapping) {
			// Updates local sorting, server OrderBy params, and persistence effect
			handleInternalSortingChange(defaultSortingState);
		} else {
			setSorting(defaultSortingState);
			onSortingChange?.(defaultSortingState);
		}
	}, [
		columns,
		defaultColumnVisibility,
		enableColumnResizing,
		resetColumnSizing,
		getDefaultSortingState,
		sortFieldMapping,
		handleInternalSortingChange,
		onSortingChange,
	]);

	// Generate column visibility panel content for TableActions
	// biome-ignore lint/correctness/useExhaustiveDependencies: columnVisibility and columnOrder needed for reactivity
	const columnVisibilityContent = useMemo(() => {
		const allColumns = table.getAllLeafColumns();
		// Sort columns by columnOrder state
		const sortedColumns = [...allColumns].sort(
			(a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id),
		);

		/** Shown in Manage columns — excludes only disableColumnReorder (e.g. actions). Row-header stays listed/draggable. */
		const manageableColumns = sortedColumns.filter(
			(col) => !isColumnReorderDisabled(col.columnDef),
		);
		const manageableColumnIds = manageableColumns.map((col) => col.id);

		const toggleableColumns = manageableColumns.filter(
			(col) =>
				col.getCanHide() && !isColumnVisibilityToggleDisabled(col.columnDef),
		);
		const areAllToggleableVisible =
			toggleableColumns.length > 0 &&
			toggleableColumns.every((col) => col.getIsVisible());

		const handleToggleAllColumns = (isChecked: boolean) => {
			const next: ColumnVisibilityState = {};
			for (const col of allColumns) {
				if (
					isColumnReorderDisabled(col.columnDef) ||
					isRowHeaderColumn(col.columnDef)
				) {
					next[col.id] = true;
				} else {
					next[col.id] = isChecked;
				}
			}
			setColumnVisibility(next);
		};

		return (
			<div
				className="flex w-full flex-col"
				data-testid="table-view-options-manage-columns"
			>
				{/* Figma DropdownSectionHeader Order=Below — separator + title + Reset */}
				<Dropdown.SectionHeader
					title={t("common.tableActions.viewOptions")}
					order="below"
					showAction
					actionLabel={t("common.actions.reset")}
					actionTestId="table-view-options-reset-btn"
					onAction={handleResetColumns}
				/>
				{/* Figma Toggle all — start-aligned (no drag-handle offset) */}
				<div className="flex w-full items-start justify-start px-(--spacing-lg,12px) pb-(--spacing-sm,6px)">
					<Checkbox
						data-testid="table-view-options-toggle-all"
						isSelected={areAllToggleableVisible}
						onChange={handleToggleAllColumns}
						label={t("common.tableActions.toggleAll")}
						className="items-center gap-(--spacing-md,8px) [&_.relative]:mt-0"
					/>
				</div>
				<DndContext
					collisionDetection={closestCenter}
					modifiers={[restrictToVerticalAxis]}
					onDragEnd={handlePopoverDragEnd}
					sensors={sensors}
				>
					<SortableContext
						items={manageableColumnIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-col gap-(--spacing-none,0px)">
							{manageableColumns.map((column) => {
								const headerDef = column.columnDef.header;
								const headerText =
									typeof headerDef === "string"
										? headerDef
										: typeof headerDef === "function"
											? column.id
											: column.id;

								return (
									<SortableColumnItem
										key={column.id}
										columnId={column.id}
										headerText={headerText}
										visibilityToggleTestId={`table-view-options-column-toggle-${column.id}`}
										isVisible={column.getIsVisible()}
										isCheckboxDisabled={isColumnVisibilityToggleDisabled(
											column.columnDef,
										)}
										onToggleVisibility={(isChecked) => {
											column.toggleVisibility(isChecked);
										}}
									/>
								);
							})}
						</div>
					</SortableContext>
				</DndContext>
			</div>
		);
	}, [
		table,
		columnVisibility,
		columnOrder,
		t,
		handlePopoverDragEnd,
		sensors,
		handleResetColumns,
	]);

	// Calculate visible column IDs for export
	// biome-ignore lint/correctness/useExhaustiveDependencies: columnVisibility needed for reactivity
	const visibleColumnIds = useMemo(() => {
		const allColumns = table.getAllLeafColumns();

		// Sort by columnOrder and filter to only visible columns
		const sortedVisibleColumns = [...allColumns]
			.sort((a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id))
			.filter((col) => col.getIsVisible())
			.map((col) => col.id);
		return sortedVisibleColumns;
	}, [table, columnVisibility, columnOrder]);

	// Enhanced loading state logic
	const showLoadingState = isLoading;
	const showFetchingIndicator = isFetching && !isLoading;
	const shouldShowRefreshButton = onRefresh && showRefreshButton !== false;

	// Default refresh button
	const defaultRefreshButton = (
		<Button
			size="sm"
			color="secondary"
			onClick={onRefresh}
			isLoading={isRefreshing}
			disabled={isRefreshing || isLoading}
			iconLeading={RefreshCw01}
		>
			{isRefreshing ? "Refreshing..." : "Refresh"}
		</Button>
	);

	// Enhanced header trailing content with refresh button
	const enhancedHeaderTrailingContent = (
		<div className="flex items-center gap-3">
			{shouldShowRefreshButton && (refreshButton || defaultRefreshButton)}
			{headerTrailingContent}
		</div>
	);

	const rows = table.getRowModel().rows as Row<AppTableFeatures, TData>[];
	const visibleColumns = table.getVisibleLeafColumns();

	const tableComponent = (
		<div ref={tableContainerRef} className="w-full">
			<CustomTable
				size={size}
				className={cx("overflow-auto", className)}
				selectionMode={enableRowSelection ? "multiple" : "none"}
				selectedKeys={selectedKeys}
				onSelectionChange={(keys) => {
					// React Aria passes Selection type which could be "all" or Set<Key>
					if (keys === "all") {
						// Select all rows - get all row IDs
						const allKeys = new Set(
							table.getRowModel().rows.map((row) => row.id),
						);
						onSelectionChange?.(allKeys);
					} else {
						onSelectionChange?.(keys as Set<string>);
					}
				}}
				title={title}
				disableOverflowWrapper={isVirtualTable}
				key={`${table
					.getVisibleLeafColumns()
					.map((col) => col.id)
					.join(",")}${enableColumnResizing ? `-v${resizeVersion}` : ""}`}
				// ref={tableRef}
			>
				<CustomTable.Header
					columns={visibleColumns.map((col) => col.columnDef)}
					// Refresh Destiny HeaderCell `sortable` whenever TanStack sorting changes
					dependencies={[sorting]}
					className="text-gray-500 border-gray-200 top-0 sticky z-20"
				>
					{(column) => {
						// Find the corresponding header from TanStack Table
						const header = table
							.getHeaderGroups()[0]
							?.headers.find((h) => h.column.id === column.id);

						if (!header) {
							// Return a placeholder head for missing headers
							return (
								<CustomTable.Head
									key={column.id}
									className="sticky top-0 z-20 bg-tertiary"
								>
									{() => <span>-</span>}
								</CustomTable.Head>
							);
						}

						const canSort = header.column.getCanSort();
						const isIconGutter = isIconGutterColumn(header.column.columnDef);
						const isSelectionGutter = isSelectionGutterColumn(
							header.column.columnDef,
						);
						const headerGroup = table.getHeaderGroups()[0];
						const isLastColumn =
							headerGroup?.headers[headerGroup.headers.length - 1]?.id ===
							header.id;
						const sortable = tableHeaderCellSortableFromColumn({
							canSort,
							isSorted: header.column.getIsSorted(),
						});
						const sortTooltip = canSort
							? header.column.getNextSortingOrder() === "asc"
								? "Sort ascending"
								: header.column.getNextSortingOrder() === "desc"
									? "Sort descending"
									: "Clear sort"
							: undefined;
						const headerLabel = <table.FlexRender header={header} />;

						return (
							<CustomTable.Head
								key={header.id}
								id={header.column.id}
								isRowHeader={
									(column.meta as { headerProps?: { isRowHeader?: boolean } })
										?.headerProps?.isRowHeader
								}
								isIconGutter={isIconGutter}
								isSelectionGutter={isSelectionGutter}
								isLastColumn={isLastColumn}
								contentAlign={
									isActionsColumnId(header.column.id) ? "end" : "start"
								}
								sortable={sortable}
								sortTooltip={sortTooltip}
								className={cx(
									"sticky top-0 z-20 bg-tertiary",
									canSort && "cursor-pointer select-none",
								)}
								style={getTableColumnLayoutStyle({
									isManuallyResized: manuallyResizedColumnIds.has(
										header.column.id,
									),
									sizePx: header.getSize(),
									forceSize: isIconGutter || isSelectionGutter,
								})}
								onClick={
									canSort ? header.column.getToggleSortingHandler() : undefined
								}
								// Only show resizer when column resizing is enabled
								resizer={
									enableColumnResizing && header.column.getCanResize() ? (
										<DeferredColumnResizer
											header={header}
											tableContainerRef={tableContainerRef}
											onResizeCommit={handleResizeCommit}
											onResizeReset={handleResizeReset}
											flushEnd={isLastColumn}
										/>
									) : undefined
								}
							>
								{() =>
									enableHeaderColumnReordering ? (
										<DraggableColumnContent
											columnId={header.column.id}
											disabled={isColumnReorderDisabled(
												header.column.columnDef,
											)}
										>
											{headerLabel}
										</DraggableColumnContent>
									) : (
										headerLabel
									)
								}
							</CustomTable.Head>
						);
					}}
				</CustomTable.Header>
				{isVirtualTable ? (
					// Keep React Aria happy: Body must receive items with id
					<CustomTable.Body items={undefined}>
						{virtualizer?.getVirtualItems()?.map((virtualRow, index) => {
							const row = rows[virtualRow.index];
							if (!row) return null;
							const isOddRow = virtualRow.index % 2 === 1;
							const isActiveRow = activeRowId != null && row.id === activeRowId;
							const rowAutoHeight = getRowAutoHeight?.(row.original) ?? false;
							return (
								<CustomTable.Row
									key={row.id}
									id={row.id}
									style={{
										height: rowAutoHeight ? undefined : `${virtualRow.size}px`,
										transform: `translateY(${
											virtualRow.start - index * virtualRow.size
										}px)`,
										...(isActiveRow
											? {
													backgroundColor: "rgba(181, 71, 8, 0.1)",
												}
											: zebraStriping && isOddRow
												? {
														backgroundColor: "var(--color-bg-secondary)",
													}
												: {}),
									}}
									className={cx(
										isRowActivatable && "cursor-pointer",
										rowAutoHeight && "h-auto! max-h-none! min-h-[50px]",
										isActiveRow &&
											"[&>td]:bg-[rgba(181,71,8,0.1)] [&>th]:bg-[rgba(181,71,8,0.1)]",
									)}
									onClick={
										isRowActivatable
											? (e) => {
													if (
														(e.target as HTMLElement).closest(
															"[data-suppress-row-click]",
														)
													)
														return;
													rowPointerActivationIds.current.add(row.id);
													activateRow(
														row.original,
														e as unknown as MouseEvent<HTMLTableRowElement>,
													);
													queueMicrotask(() => {
														rowPointerActivationIds.current.delete(row.id);
													});
												}
											: undefined
									}
									onAction={
										isRowActivatable
											? () => {
													if (rowPointerActivationIds.current.has(row.id))
														return;
													activateRow(row.original);
												}
											: undefined
									}
								>
									{row.getVisibleCells().map((cell) => {
										const isIconGutter = isIconGutterColumn(
											cell.column.columnDef,
										);
										const isSelectionGutter = isSelectionGutterColumn(
											cell.column.columnDef,
										);
										return (
											<CustomTable.Cell
												key={cell.id}
												isIconGutter={isIconGutter}
												isSelectionGutter={isSelectionGutter}
												allowAutoHeight={rowAutoHeight}
												style={getTableColumnLayoutStyle({
													isManuallyResized: manuallyResizedColumnIds.has(
														cell.column.id,
													),
													sizePx: cell.column.getSize(),
													forceSize: isIconGutter || isSelectionGutter,
												})}
											>
												{/* AppCell must sit *inside* Aria Cell — Collection can re-parent Cells. */}
												<table.AppCell cell={cell}>
													{() => {
														const cellContent = wrapAccessTableCellContent(
															cell.column.id,
															cell.column.columnDef.meta,
															cell,
															wrapTableCellClamp(
																cell.column.id,
																cell.column.columnDef.meta,
																cell,
																<table.FlexRender cell={cell} />,
															),
														);
														return enableHeaderColumnReordering ? (
															<DraggableCellContent
																columnId={cell.column.id}
																isDragging={activeColumnId === cell.column.id}
															>
																{cellContent}
															</DraggableCellContent>
														) : (
															cellContent
														);
													}}
												</table.AppCell>
											</CustomTable.Cell>
										);
									})}
								</CustomTable.Row>
							);
						})}
					</CustomTable.Body>
				) : (
					// Key busts React Aria Collection's per-item render cache when the
					// open-drawer active row changes (items identity alone does not).
					<CustomTable.Body
						key={`active-drawer-row:${activeRowId ?? ""}`}
						items={rows}
					>
						{(row) => {
							const rowIndex = rows.findIndex((r) => r.id === row.id);
							const isOddRow = rowIndex % 2 === 1;
							const isActiveRow = activeRowId != null && row.id === activeRowId;
							const rowAutoHeight = getRowAutoHeight?.(row.original) ?? false;
							return (
								<CustomTable.Row
									key={row.id}
									id={row.id}
									highlightSelectedRow={enableRowSelection}
									onClick={
										isRowActivatable
											? (e) => {
													if (
														(e.target as HTMLElement).closest(
															"[data-suppress-row-click]",
														)
													)
														return;
													rowPointerActivationIds.current.add(row.id);
													activateRow(
														row.original,
														e as unknown as MouseEvent<HTMLTableRowElement>,
													);
													queueMicrotask(() => {
														rowPointerActivationIds.current.delete(row.id);
													});
												}
											: undefined
									}
									onAction={
										isRowActivatable
											? () => {
													if (rowPointerActivationIds.current.has(row.id))
														return;
													activateRow(row.original);
												}
											: undefined
									}
									className={cx(
										isRowActivatable && "cursor-pointer",
										rowAutoHeight && "h-auto! max-h-none! min-h-[50px]",
										isActiveRow &&
											"bg-[rgba(181,71,8,0.1)] [&>td]:bg-[rgba(181,71,8,0.1)] [&>th]:bg-[rgba(181,71,8,0.1)]",
										!isActiveRow &&
											zebraStriping &&
											isOddRow &&
											"bg-secondary hover:bg-[rgba(181,71,8,0.05)]",
									)}
								>
									{row.getVisibleCells().map((cell) => {
										const isIconGutter = isIconGutterColumn(
											cell.column.columnDef,
										);
										const isSelectionGutter = isSelectionGutterColumn(
											cell.column.columnDef,
										);
										const cellProps = (
											cell.column.columnDef.meta as {
												cellProps?: Record<string, unknown>;
											}
										)?.cellProps;
										return (
											<CustomTable.Cell
												key={cell.id}
												isIconGutter={isIconGutter}
												isSelectionGutter={isSelectionGutter}
												allowAutoHeight={rowAutoHeight}
												{...(cellProps ?? {})}
												style={{
													...getTableColumnLayoutStyle({
														isManuallyResized: manuallyResizedColumnIds.has(
															cell.column.id,
														),
														sizePx: cell.column.getSize(),
														forceSize: isIconGutter || isSelectionGutter,
													}),
													...(typeof cellProps?.style === "object"
														? (cellProps.style as CSSProperties)
														: {}),
												}}
											>
												{/* AppCell must sit *inside* Aria Cell — Collection can re-parent Cells. */}
												<table.AppCell cell={cell}>
													{() => {
														const cellContent = wrapAccessTableCellContent(
															cell.column.id,
															cell.column.columnDef.meta,
															cell,
															wrapTableCellClamp(
																cell.column.id,
																cell.column.columnDef.meta,
																cell,
																<table.FlexRender cell={cell} />,
															),
														);
														return enableHeaderColumnReordering ? (
															<DraggableCellContent
																columnId={cell.column.id}
																isDragging={activeColumnId === cell.column.id}
															>
																{cellContent}
															</DraggableCellContent>
														) : (
															cellContent
														);
													}}
												</table.AppCell>
											</CustomTable.Cell>
										);
									})}
								</CustomTable.Row>
							);
						}}
					</CustomTable.Body>
				)}
			</CustomTable>
		</div>
	);

	// Get column IDs for SortableContext (header drag)
	const columnIds = useMemo(
		() => visibleColumns.map((col) => col.id),
		[visibleColumns],
	);

	// Get the header text for the active column (for DragOverlay)
	const activeColumnHeader = useMemo(() => {
		if (!activeColumnId) return null;
		const column = visibleColumns.find((col) => col.id === activeColumnId);
		if (!column) return null;
		const headerDef = column.columnDef.header;
		return typeof headerDef === "string" ? headerDef : column.id;
	}, [activeColumnId, visibleColumns]);

	// Wrap table in DndContext when header column reordering is enabled
	const tableWithDnd = enableHeaderColumnReordering ? (
		<DndContext
			collisionDetection={closestCenter}
			modifiers={[restrictToHorizontalAxis]}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			sensors={sensors}
		>
			<SortableContext
				items={columnIds}
				strategy={horizontalListSortingStrategy}
			>
				{tableComponent}
			</SortableContext>
			<DragOverlay dropAnimation={null}>
				{activeColumnId ? (
					<DragOverlayHeader>
						<span className="text-xs font-semibold text-quaternary whitespace-nowrap">
							{activeColumnHeader}
						</span>
					</DragOverlayHeader>
				) : null}
			</DragOverlay>
		</DndContext>
	) : (
		tableComponent
	);

	// Figma TableView Loading (3106:17220 / 3106:17301) — centered xl spinner in 500px region
	const defaultLoadingContent = (
		<div className="flex min-h-[500px] w-full items-center justify-center">
			<LoadingIndicator type="line-simple" size="xl" supportingText />
		</div>
	);

	// Show fetching indicator if background fetching
	const fetchingIndicator = showFetchingIndicator && (
		<div className="flex items-center justify-center py-2">
			<div className="flex items-center gap-2 text-sm text-tertiary">
				<div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
				<span>Updating...</span>
			</div>
		</div>
	);

	const tablePagination = showPagination ? (
		<TablePagination
			pageIndex={pagination.pageIndex}
			pageSize={pagination.pageSize}
			pageCount={
				paginationMode === "server"
					? (externalPageCount ?? 0)
					: table.getPageCount()
			}
			totalRowCount={rowCount ?? totalRowCount}
			selectedCount={selectedCount}
			onPaginationChange={handlePaginationChange}
			pageSizeOptions={pageSizeOptions}
		/>
	) : null;

	// Figma TableView Empty Search (4341:90653) — filter bar shows "No Results"
	// when search/filters return zero rows (not the zero-data Empty state).
	const hasActiveSearchOrFilters =
		internalFilterValue.trim().length > 0 || hasActiveFilters;
	const hasFilterHandlers = Boolean(onFilterChange || onDebouncedFilterChange);
	const isBarFilterLayout = filterLayout === "bar";
	const hasInlineFilters = filtersTrailingContent != null;
	const showHideFiltersAction =
		(isBarFilterLayout && hasInlineFilters) || Boolean(onFilterClick);
	const filterOpenForActions =
		isBarFilterLayout && hasInlineFilters
			? areInlineFiltersVisible
			: isFilterOpen;
	const handleTableFilterClick = () => {
		if (isBarFilterLayout && hasInlineFilters) {
			setAreInlineFiltersVisible((visible) => !visible);
			return;
		}
		onFilterClick?.();
	};
	// Figma Empty / Error — disable search + filters when there is no data to
	// filter. Empty Search keeps the bar interactive so users can adjust criteria.
	// Do NOT disable while loading: server search flips `isLoading` on query-key
	// change, and disabling the input drops focus mid-typing.
	const unfilteredTotal = totalRowCount ?? rowCount ?? data?.length ?? 0;
	const isZeroDataEmpty = unfilteredTotal === 0 && !hasActiveSearchOrFilters;
	const isFilterBarDisabled = isZeroDataEmpty;
	// Empty Search (No Results) — keep search/filters, hide options + Add.
	const isEmptySearchResults =
		!showLoadingState && hasActiveSearchOrFilters && (data?.length ?? 0) === 0;
	const showFilterBarTrailing = !isFilterBarDisabled && !isEmptySearchResults;

	const filterBarSearch = (
		<Input
			data-testid={filterInputTestId}
			clearButtonTestId={
				filterInputTestId ? `${filterInputTestId}-clear-btn` : undefined
			}
			placeholder={filterPlaceholder}
			value={String(internalFilterValue)}
			onChange={(value) => handleFilterInputChange(value)}
			icon={SearchMd}
			size="sm"
			className="w-full"
			showClearButton={internalFilterValue !== ""}
			isDisabled={isFilterBarDisabled}
		/>
	);

	if (showCard) {
		const filterBar =
			isBarFilterLayout && enableTableActions && hasFilterHandlers ? (
				<TableFilterBarDisabledContext.Provider value={isFilterBarDisabled}>
					<TableFilterBar
						type={filterBarType}
						search={filterBarSearch}
						filters={
							areInlineFiltersVisible ? filtersTrailingContent : undefined
						}
						trailing={
							showFilterBarTrailing ? (
								filterBarType === TableFilterBarType.Workspace ? (
									<>
										<TableActions
											variant="inline"
											showFilterButton={showHideFiltersAction}
											isFilterOpen={filterOpenForActions}
											hasActiveFilters={hasActiveFilters}
											hasActiveSearch={internalFilterValue.trim().length > 0}
											onFilterClick={handleTableFilterClick}
											onExportClick={onExportClick}
											isExportLoading={isExportLoading}
											viewOptionsContent={columnVisibilityContent}
											visibleColumns={visibleColumnIds}
											rowCount={rowCount}
										/>
										{filterBarTrailing}
									</>
								) : (
									filterBarTrailing
								)
							) : undefined
						}
						className={filterBarClassName}
					/>
				</TableFilterBarDisabledContext.Provider>
			) : (
				<>
					{enableTableActions && (
						<div className="flex flex-row justify-end">
							<TableActions
								isFilterOpen={isFilterOpen}
								hasActiveFilters={hasActiveFilters}
								hasActiveSearch={internalFilterValue.trim().length > 0}
								onFilterClick={onFilterClick}
								onExportClick={onExportClick}
								isExportLoading={isExportLoading}
								viewOptionsContent={columnVisibilityContent}
								visibleColumns={visibleColumnIds}
								rowCount={rowCount}
							/>
						</div>
					)}

					{enableTableActions && hasFilterHandlers && (
						<FilterDrawer
							isOpen={isFilterOpen}
							value={internalFilterValue}
							onChange={handleFilterInputChange}
							debounce={0} // Wrapper handles debouncing
							placeholder={filterPlaceholder}
							filtersTrailingContent={filtersTrailingContent}
							searchInputTestId={filterInputTestId}
							searchMaxWidthPercent={filterSearchMaxWidthPercent}
						/>
					)}
				</>
			);

		const hasPopulatedRows = !showLoadingState && (data?.length ?? 0) > 0;
		const tableBody = showLoadingState
			? (loadingState ?? defaultLoadingContent)
			: hasPopulatedRows
				? tableWithDnd
				: (emptyState ?? null);

		// Figma `_TableOptions` (4962:186000) — Download + Manage Columns in one dropdown.
		// Landing: portal into PageHeader overflow; Workspace: filter-bar trailing.
		const headerTableOptions =
			filterBarType === TableFilterBarType.Landing &&
			tableOptionsPortalTarget != null &&
			(onExportClick || columnVisibilityContent || showHideFiltersAction)
				? createPortal(
						<TableActions
							variant="header"
							showFilterButton={showHideFiltersAction}
							isFilterOpen={filterOpenForActions}
							hasActiveFilters={hasActiveFilters}
							hasActiveSearch={internalFilterValue.trim().length > 0}
							onFilterClick={handleTableFilterClick}
							onExportClick={onExportClick}
							isExportLoading={isExportLoading}
							viewOptionsContent={columnVisibilityContent}
							visibleColumns={visibleColumnIds}
							rowCount={rowCount}
						/>,
						tableOptionsPortalTarget,
					)
				: null;

		// Figma TableView: filter bar → populated bordered table + pagination outside.
		// Empty / Loading / Error / Empty Search sit below the filter bar without a table shell.
		return (
			<table.AppTable>
				<div className={cx("flex w-full flex-col isolate", className)}>
					{filterBar}
					{hasPopulatedRows ? (
						<>
							<CustomTableCard.Root className={cardClassName}>
								<CustomTableCard.Header
									description={description}
									badge={badge}
									contentTrailing={enhancedHeaderTrailingContent}
									showPadding={showHeaderPadding}
								/>
								{fetchingIndicator}
								{tableBody}
							</CustomTableCard.Root>
							{tablePagination}
						</>
					) : (
						tableBody
					)}
					{headerTableOptions}
				</div>
			</table.AppTable>
		);
	}
	return <table.AppTable>{tableWithDnd}</table.AppTable>;
}
